import { useEffect, useRef } from 'react';
import { WizardState } from '~/components/organism/Wizard/context/WizardContext';
import type { StepId } from '~/components/organism/WizardSteps';
import useTrackEvent from '~/hooks/useTrackEvent';

const ONBOARDING_WIZARD_ID = 'onboarding-wizard';

type Args = {
  wizardId: string;
  wizardState: WizardState;
};

type StepTrackingOutput = {
  enabled_apps?: Array<string>;
  enabled_apps_count?: number;
  failed_apps?: Array<string>;
  failed_apps_count?: number;
};

/**
 * Extracts relevant output data for tracking based on step ID
 */
const extractStepOutput = (
  stepId: StepId,
  outputMap: WizardState['outputMap'],
): StepTrackingOutput => {
  const stepOutput = outputMap[stepId];
  if (!stepOutput) return {};

  const output: StepTrackingOutput = {};

  switch (stepId) {
    case 'LoadingScreen': {
      if (
        'enabledAppsOutput' in stepOutput &&
        stepOutput.enabledAppsOutput?.values
      ) {
        output.enabled_apps = stepOutput.enabledAppsOutput.values.map(
          app => app.label,
        );
        output.enabled_apps_count = stepOutput.enabledAppsOutput.values.length;
      }
      if (
        'failedAppsOutput' in stepOutput &&
        stepOutput.failedAppsOutput?.values
      ) {
        output.failed_apps = stepOutput.failedAppsOutput.values.map(
          app => app.label,
        );
        output.failed_apps_count = stepOutput.failedAppsOutput.values.length;
      }
      break;
    }
    default:
      break;
  }

  return output;
};

/**
 * Hook to track onboarding step views and completions.
 * Should be used at the Wizard component level to automatically track all steps.
 * Only tracks when the wizard ID matches the onboarding wizard ID.
 *
 * Tracking behavior:
 * - `onboarding_step_viewed`: Fired when a step becomes the current step (only once per step)
 * - `onboarding_step_completed`: Fired when navigating forward to next step via "Next" button
 * - `onboarding_step_skipped`: Fired when navigating forward via "Skip" button (not counted as completed)
 *
 * Navigation behavior:
 * - Going back to a previous step does NOT re-track "viewed" (already tracked)
 * - Going back does NOT mark steps as completed/skipped
 * - Only forward navigation (step index increases) triggers completion/skip tracking
 *
 * Drop-off detection:
 * - Steps with `onboarding_step_viewed` but no `onboarding_step_completed` or `onboarding_step_skipped` = user dropped off
 * - The last step that was viewed but not completed/skipped indicates where the user dropped off
 * - If wizard is closed/hidden, the current step will have "viewed" but no "completed"/"skipped" event
 */
const useOnboardingStepTracking = ({ wizardId, wizardState }: Args) => {
  const trackEvent = useTrackEvent();

  // Track which steps have been viewed, completed, and skipped
  const stepTrackingRef = useRef<
    Record<
      string,
      {
        viewed: boolean;
        completed: boolean;
        skipped: boolean;
        startTime: number | null;
        // Store output state when step was viewed
        outputWhenViewed?: string;
      }
    >
  >({});

  const previousStepIdRef = useRef<StepId | null>(null);
  const previousStepIndexRef = useRef<number | null>(null);

  // Only track if this is the onboarding wizard and wizard is showing
  const isOnboardingWizard = wizardId === ONBOARDING_WIZARD_ID;
  const isWizardShowing = wizardState.show === true;

  // Track step viewed when current step changes
  useEffect(() => {
    if (!isOnboardingWizard || !isWizardShowing) return;

    const currentStep = wizardState.steps[wizardState.currentStep];
    const currentStepId = currentStep?.id;
    const currentStepName = currentStep?.title;

    if (!currentStepId || !currentStepName) return;

    // Initialize tracking for this step if not already tracked
    if (!stepTrackingRef.current[currentStepId]) {
      stepTrackingRef.current[currentStepId] = {
        viewed: false,
        completed: false,
        skipped: false,
        startTime: null,
        outputWhenViewed: undefined,
      };
    }

    // Track viewed if not already tracked
    if (!stepTrackingRef.current[currentStepId].viewed) {
      stepTrackingRef.current[currentStepId].viewed = true;
      stepTrackingRef.current[currentStepId].startTime = Date.now();
      // Store the output state when step is first viewed (to detect if it changes)
      stepTrackingRef.current[currentStepId].outputWhenViewed = JSON.stringify(
        wizardState.outputMap[currentStepId],
      );

      // Extract relevant output data for this step
      const stepOutput = extractStepOutput(
        currentStepId,
        wizardState.outputMap,
      );

      /** ----- TRACKING: onboarding_step_viewed ----- */
      trackEvent({
        eventName: 'onboarding_step_viewed',
        parameters: {
          step_id: currentStepId,
          step_name: String(currentStepName),
          ...stepOutput,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    wizardState.currentStep,
    wizardState.currentSubStep,
    isOnboardingWizard,
    isWizardShowing,
  ]);

  // Track step completed/skipped when navigating away from a step
  useEffect(() => {
    if (!isOnboardingWizard || !isWizardShowing) return;

    const currentStep = wizardState.steps[wizardState.currentStep];
    const currentStepId = currentStep?.id;
    const currentStepIndex = wizardState.currentStep;

    if (!currentStepId) return;

    // Initialize previousStepIdRef on first render
    if (previousStepIdRef.current === null) {
      previousStepIdRef.current = currentStepId;
      previousStepIndexRef.current = currentStepIndex;
      return;
    }

    // Only track completion/skip when navigating forward (not backward)
    const isNavigatingForward =
      previousStepIndexRef.current !== null &&
      currentStepIndex > previousStepIndexRef.current;

    // If we were on a different step before and navigating forward
    if (
      previousStepIdRef.current !== currentStepId &&
      isNavigatingForward &&
      stepTrackingRef.current[previousStepIdRef.current]
    ) {
      const previousStepTracking =
        stepTrackingRef.current[previousStepIdRef.current];
      const previousStep =
        wizardState.steps.find(step => step.id === previousStepIdRef.current) ??
        null;
      const previousStepName = previousStep?.title;

      // Check if step was skipped (was skippable and output didn't change from when viewed)
      const wasSkippable = previousStep?.skippable === true;
      const currentOutput = JSON.stringify(
        wizardState.outputMap[previousStepIdRef.current],
      );
      const outputWhenViewed =
        previousStepTracking.outputWhenViewed ?? 'undefined';
      // Step was skipped if it was skippable AND output didn't change (user didn't complete it)
      const wasSkipped = wasSkippable && currentOutput === outputWhenViewed;

      if (
        !previousStepTracking.completed &&
        !previousStepTracking.skipped &&
        previousStepTracking.startTime !== null
      ) {
        const timeSpent = Math.round(
          (Date.now() - previousStepTracking.startTime) / 1000,
        );

        // Extract relevant output data for the previous step
        const stepOutput = extractStepOutput(
          previousStepIdRef.current,
          wizardState.outputMap,
        );

        if (wasSkipped) {
          // Mark as skipped (not completed)
          previousStepTracking.skipped = true;

          /** ----- TRACKING: onboarding_step_skipped ----- */
          trackEvent({
            eventName: 'onboarding_step_skipped',
            parameters: {
              step_id: previousStepIdRef.current,
              step_name: String(previousStepName || previousStepIdRef.current),
              time_spent_seconds: timeSpent,
              ...stepOutput,
            },
          });
        } else {
          // Mark as completed (not skipped)
          previousStepTracking.completed = true;
          /** ----- TRACKING: onboarding_step_completed ----- */
          trackEvent({
            eventName: 'onboarding_step_completed',
            parameters: {
              step_id: previousStepIdRef.current,
              step_name: String(previousStepName || previousStepIdRef.current),
              time_spent_seconds: timeSpent,
              ...stepOutput,
            },
          });
        }
      }
    }

    // Update the previous step references
    previousStepIdRef.current = currentStepId;
    previousStepIndexRef.current = currentStepIndex;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    wizardState.currentStep,
    wizardState.currentSubStep,
    wizardState.outputMap,
    isOnboardingWizard,
    isWizardShowing,
  ]);
};

export default useOnboardingStepTracking;
